import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { faSparkles } from '@fortawesome/pro-light-svg-icons/faSparkles';
import { ASSET_SPECS } from 'fym-shared/src/music24/assetSpecs';
import { needsFullVideoPlan, StudioThemeId, themeOutputs } from 'fym-shared/src/music24/studioThemes';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { ResolvedAssetStyle } from 'fym-shared/src/music24/assetStyles';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { Panel, PanelHead } from '../../../components/primitives/Panel';
import { useMusic24Capabilities } from '../../../hooks/useMusic24Capabilities';
import { StudioCreditBlock } from '../../credits/StudioCreditBlock';
import { StudioStickyMakeBar } from '../../credits/StudioStickyMakeBar';
import { useIsNarrowCreditBar } from '../../credits/useIsNarrowCreditBar';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';
import { StudioDetails } from '../studioRules';
import { studioMessages, studioMomentMessages } from '../studioMessages';
import { studioThemeMessages } from '../studioThemeMessages';
import { kitShapeMessages } from '../../kitLabels';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import { assetStyleMessages, waveformShapeMessages } from '../../themes/assetStyleMessages';
import { StudioActionFeedback } from '../studioActionErrorText';
import { StudioActionError } from '../StudioActionError';
import { imageTileMessages } from './imageTiles/imageTileMessages';
import { WatermarkCallout } from '../../../watermarkUpsell/files/WatermarkCallout';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Studio; each part uses its own slice of it */
import styles from '../Studio.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StudioRailProps {
  accountId: number;
  themeId: StudioThemeId;
  /** The theme of the song. */
  releaseThemeId: ReleaseThemeId;
  /** The style of the asset, or null for an asset type with no styles. */
  style: ResolvedAssetStyle | null;
  details: StudioDetails;
  /** The credits "Make it" uses; 0 for images. */
  credits: number;
  canMake: boolean;
  isMaking: boolean;
  onMake: () => void;
  /** The drawn preview of the asset (designs s7, s7b). Below 700 px it shows at the top of the page instead. */
  preview?: React.ReactNode;
  /** A short, true line under the preview (design studio-details a1). */
  previewNote?: string;
  /** More summary rows (the length and the file of a full video, the lines of a lyric moment). */
  extraRows?: ReadonlyArray<[string, string]>;
  /** The line under "Make it": the time it takes (designs s7, s7b, s10). */
  wait?: string | null;
  /** Why "Make it" is off (the Artist plan, the clip bank), above the button. */
  notice?: React.ReactNode;
  /** Why "Make it" is off when a field is not ready (for example lyric lines with no time). */
  blockedReason?: string | null;
  /** The answer of the last "Make it" when it made nothing: it shows next to the button. */
  actionFeedback?: StudioActionFeedback | null;
  /**
   * The full-width panel of an image with no choice: the previews on the left, the summary and
   * "Make it" on the right (under the previews on a narrow screen). The previews show on a phone too.
   */
  wide?: boolean;
}

/**
 * The right rail of step 3 (designs n1–n3b, q3b, q3c, s7, s7b): the asset and the size of the file,
 * the preview, the theme of the song, the style, the choices, the credit block (t5), the watermark
 * callout of Free and the trial (design: trial-upsell a6), "Make it · N credits" and the time it takes. Below 700 px the cost, the balance
 * and "Make it" stick to the bottom of the page as one bar (t5b).
 *
 * When "Make it" is off, the rail says why (`notice`, `blockedReason`): a grey button next to the
 * credit box must never read as "no credits". When the balance (plan plus packs) is really below
 * the cost, the button reads "Buy credits to make it" and opens the credit packs at once (owner
 * rule 2026-10-06); a member and a native web view get the owner note or a neutral line.
 *
 * A failed "Make it" shows its sentence directly under the button (`actionFeedback`), never at the
 * top of the page. Below 700 px it shows above the sticky bar.
 */
const StudioRailComponent: React.FC<StudioRailProps> = (props) => {
  const { accountId, themeId, releaseThemeId, style, details, credits, canMake, isMaking, onMake } = props;
  const { preview, previewNote, extraRows, wait, notice, blockedReason, actionFeedback = null } = props;
  const { wide = false } = props;
  const intl = useIntl();
  const gate = useVideoCreditGate(accountId);
  const isShort = gate.isShort(credits);
  const isNarrow = useIsNarrowCreditBar();
  const needsPlan = needsFullVideoPlan(themeId);
  const { clipWatermark } = useMusic24Capabilities();
  const assetName = intl.formatMessage(studioThemeMessages[themeId].name);
  const spec =
    ASSET_SPECS[themeOutputs(themeId, { moment: details.moment, shape: details.shape })[0]?.specId ?? 'story916'];

  // The button sits in the rail, or in the sticky bar below 700 px (design t5b).
  const makeButton = (isWide: boolean) => (
    <SMLButton
      color="primary"
      size={isWide ? 'lg' : 'md'}
      stretch={isWide}
      omitCap
      leftIcon={faSparkles}
      disabled={!canMake}
      isLoading={isMaking}
      onClick={isShort ? gate.offerCredits : onMake}
    >
      {isShort
        ? gate.shortLabel
        : credits > 0
          ? intl.formatMessage(studioMessages.makeItCredits, { count: credits })
          : intl.formatMessage(studioMessages.makeIt)}
    </SMLButton>
  );

  const rows = useMemo(() => {
    const list: Array<[string, string]> = [
      [intl.formatMessage(studioMessages.railTheme), intl.formatMessage(releaseThemeMessages[releaseThemeId].name)],
    ];
    if (style !== null) {
      const styleName = intl.formatMessage(assetStyleMessages[style.style].name);
      list.push([
        intl.formatMessage(studioMessages.railStyle),
        style.waveform === null
          ? styleName
          : `${styleName} · ${intl.formatMessage(waveformShapeMessages[style.waveform])}`,
      ]);
    }
    if (details.moment !== undefined) {
      list.push([
        intl.formatMessage(studioMessages.momentTitle),
        // The Story moment `announce` is the countdown (design studio-details a1).
        intl.formatMessage(
          themeId === 'story' && details.moment === 'announce'
            ? imageTileMessages.momentCountdown
            : studioMomentMessages[details.moment]
        ),
      ]);
    }
    if (details.shape !== undefined) {
      list.push([intl.formatMessage(studioMessages.shapeTitle), intl.formatMessage(kitShapeMessages[details.shape])]);
    }
    if (details.lengthSec !== undefined) {
      list.push([
        intl.formatMessage(studioMessages.lengthTitle),
        intl.formatMessage(studioMessages.seconds, { count: details.lengthSec }),
      ]);
    }
    return extraRows === undefined ? list : [...list, ...extraRows];
  }, [details, extraRows, intl, releaseThemeId, style, themeId]);

  const note = previewNote !== undefined && <p className={styles.railNote}>{previewNote}</p>;
  const summaryRows = rows.map(([label, value]) => (
    <div key={label} className={styles.sumRow}>
      <span>{label}</span>
      <span className={styles.sumValue}>{value}</span>
    </div>
  ));
  const actions = (
    <>
      {!isNarrow && (
        <StudioCreditBlock
          accountId={accountId}
          cost={credits}
          needsPlan={needsPlan}
          showShortNote={canMake}
          shortNoteTestId="studio-rail-short"
        />
      )}
      {/* design: trial-upsell a6. A video costs credits; an image costs none and gets no callout. */}
      {clipWatermark && credits > 0 && <WatermarkCallout variant="make" surface="make_dialog" credits={credits} />}
      {notice}
      {!canMake && !isMaking && blockedReason !== undefined && blockedReason !== null && (
        <span className="small text-warning" data-testid="studio-rail-blocked">
          {blockedReason}
        </span>
      )}
      {isNarrow && <StudioActionError feedback={actionFeedback} />}
      {isNarrow ? (
        <StudioStickyMakeBar
          accountId={accountId}
          cost={credits}
          needsPlan={needsPlan}
          showShortNote={canMake}
          shortNoteTestId="studio-rail-short"
        >
          {makeButton(false)}
        </StudioStickyMakeBar>
      ) : (
        <>
          {makeButton(true)}
          <StudioActionError feedback={actionFeedback} />
        </>
      )}
      {wait !== undefined && wait !== null && <p className={styles.railWait}>{wait}</p>}
    </>
  );

  return (
    <div className={clsx(styles.rail, wide && styles.railWide)} data-testid="studio-rail" data-wide={wide}>
      <Panel>
        <PanelHead title={assetName} right={<span className={styles.mono}>{`${spec.width} × ${spec.height}`}</span>} />
        {wide ? (
          <div className={styles.railWideBody}>
            {preview !== undefined && (
              <div className={styles.railWidePreview} data-testid="studio-rail-wide-preview">
                {preview}
              </div>
            )}
            <div className={styles.railSummary}>
              {summaryRows}
              {note}
              {actions}
            </div>
          </div>
        ) : (
          <div className={styles.railBody}>
            {preview !== undefined && <div className={styles.railPreview}>{preview}</div>}
            {note}
            {summaryRows}
            {actions}
          </div>
        )}
      </Panel>
    </div>
  );
};

export const StudioRail = TypedMemo(StudioRailComponent);
